import { Plus } from 'lucide-react';

interface SettingsEmptyStateProps {
  /** One or more icons with their brand colors */
  icons: Array<{ icon: React.ReactNode; color: string }>;
  /** Main heading */
  title: string;
  /** Description text */
  description: string;
  /** CTA button label (default: "Get Started") */
  buttonLabel?: string;
  /** CTA button click handler — omit to hide button */
  onAction?: () => void;
}

/**
 * Shared empty state for all Settings sub-pages.
 * Renders floating provider/feature icons, a heading, description,
 * and optional CTA button.
 */
export function SettingsEmptyState({
  icons,
  title,
  description,
  buttonLabel = 'Get Started',
  onAction,
}: SettingsEmptyStateProps) {
  return (
    <div style={{
      textAlign: 'center', padding: '4rem 2rem',
      border: '1px dashed var(--color-border)',
      borderRadius: 16, background: 'var(--color-surface)',
    }}>
      {/* Floating icons */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 14, marginBottom: 24 }}>
        {icons.map((item, i) => (
          <div key={i} style={{
            width: 46, height: 46, borderRadius: 13,
            background: `${item.color}15`, border: `1px solid ${item.color}30`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: item.color,
            animation: `settingsEmptyFloat ${1.5 + i * 0.3}s ease-in-out infinite alternate`,
          }}>
            {item.icon}
          </div>
        ))}
      </div>

      <h3 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 8px', color: 'var(--color-text)' }}>
        {title}
      </h3>
      <p style={{
        color: 'var(--color-text-3)', maxWidth: 420, margin: '0 auto',
        lineHeight: 1.6, fontSize: 13,
      }}>
        {description}
      </p>

      {onAction && (
        <button
          onClick={onAction}
          style={{
            marginTop: 24, padding: '10px 22px', borderRadius: 9,
            border: 'none', background: 'var(--color-primary)', color: '#fff',
            fontWeight: 600, fontSize: 13, cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(99,102,241,0.3)',
            display: 'inline-flex', alignItems: 'center', gap: 7,
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(99,102,241,0.4)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 16px rgba(99,102,241,0.3)'; }}
        >
          <Plus size={15} /> {buttonLabel}
        </button>
      )}

      <style>{`
        @keyframes settingsEmptyFloat {
          from { transform: translateY(0); }
          to   { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
}
